import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type RefObject,
} from "react";
import { nanoid } from "nanoid";
import { Trash2 } from "lucide-react";
import {
  widgetTargetKey,
  type Stakeholder,
  type StakeholderMapParams,
  type StakeholderQuadrant,
} from "@playbookai/widget-schemas";
import type { PeopleSource } from "@playbookai/types";
import { FormField } from "../../molecules/FormField/FormField.js";
import { PeopleField } from "../../molecules/PeopleField/PeopleField.js";
import { Popover, PopoverContent } from "../../molecules/Popover/index.js";
import { Tooltip } from "../../molecules/Tooltip/Tooltip.js";
import type { WidgetBodyProps, WidgetChangeOptions } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { personFieldValue, widgetPerson } from "../person-field.js";
import { usePeopleSource } from "../user-directory.js";

const QUADRANTS: Array<{
  key: StakeholderQuadrant;
  label: string;
  caption: string;
  cellBg: string;
  chip: string;
}> = [
  {
    key: "satisfy",
    label: "Keep Satisfied",
    caption: "High power · Low interest",
    cellBg: "bg-pb-amber/8",
    chip: "bg-pb-amber/15 text-pb-amber-text border-pb-amber/40",
  },
  {
    key: "manage",
    label: "Manage Closely",
    caption: "High power · High interest",
    cellBg: "bg-pb-red/10",
    chip: "bg-pb-red/15 text-pb-red-text border-pb-red/40",
  },
  {
    key: "monitor",
    label: "Monitor",
    caption: "Low power · Low interest",
    cellBg: "bg-pb-card-elevated/60",
    chip: "bg-pb-card-elevated text-pb-text-muted border-pb-border",
  },
  {
    key: "inform",
    label: "Keep Informed",
    caption: "Low power · High interest",
    cellBg: "bg-pb-teal/8",
    chip: "bg-pb-teal/15 text-pb-teal-text border-pb-teal/40",
  },
];

const CHIP_BY_QUADRANT: Record<StakeholderQuadrant, string> = QUADRANTS.reduce(
  (acc, quadrant) => {
    acc[quadrant.key] = quadrant.chip;
    return acc;
  },
  {} as Record<StakeholderQuadrant, string>,
);

const QUAD_CENTER: Record<StakeholderQuadrant, { x: number; y: number }> = {
  satisfy: { x: 0.25, y: 0.25 },
  manage: { x: 0.75, y: 0.25 },
  monitor: { x: 0.25, y: 0.75 },
  inform: { x: 0.75, y: 0.75 },
};

const clamp = (value: number, low: number, high: number) =>
  Math.max(low, Math.min(high, value));

function quadrantFromPosition(x: number, y: number): StakeholderQuadrant {
  if (y < 0.5) return x > 0.5 ? "manage" : "satisfy";
  return x > 0.5 ? "inform" : "monitor";
}

export function placeInQuadrant(
  quadrant: StakeholderQuadrant,
  index: number,
  count: number,
): { leftPct: number; topPct: number } {
  const center = QUAD_CENTER[quadrant];
  const yOffset = (index - (count - 1) / 2) * 0.13;
  return {
    leftPct: center.x * 100,
    topPct: clamp(center.y + yOffset, 0.08, 0.92) * 100,
  };
}

export interface StakeholderDraft {
  name: string;
  /** The member `name` names, when picked (assign-person d7); else null. */
  userId: string | null;
  role: string;
  quadrant: StakeholderQuadrant;
}

/** Only a pick of a real person may add them as Viewer (d25). */
function grantFor(
  userId: string | null,
  storedUserId: string | null | undefined,
): WidgetChangeOptions | undefined {
  return userId && userId !== (storedUserId ?? null)
    ? { grantViewerIfMissing: true }
    : undefined;
}

function refusal(reason: unknown): string {
  return reason instanceof Error && reason.message
    ? reason.message
    : "Edit not saved";
}

/**
 * The stakeholder's name as the shared person field, on the canvas's
 * `PeopleSource`: a picked member or a typed name. A save's refusal shows
 * under it and the form stays open.
 */
function StakeholderNameField({
  source,
  draft,
  storedUserId,
  error,
  disabled,
  onChange,
  onFocus,
  onBlur,
}: {
  source: PeopleSource;
  draft: StakeholderDraft;
  storedUserId: string | null | undefined;
  error: string | null;
  disabled?: boolean | undefined;
  onChange: (next: Pick<StakeholderDraft, "name" | "userId">) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}): ReactElement {
  return (
    <FormField {...(error ? { error } : {})}>
      <PeopleField
        value={personFieldValue(draft.name, draft.userId)}
        onChange={(next) => {
          const person = widgetPerson(next);
          onChange({ name: person.text, userId: person.userId });
        }}
        source={source}
        label="Stakeholder"
        allowTyped
        grantTiming="save"
        pendingGrant={grantFor(draft.userId, storedUserId) !== undefined}
        disabled={disabled}
        {...(onFocus ? { onFocus } : {})}
        {...(onBlur ? { onBlur } : {})}
      />
    </FormField>
  );
}

function StakeholderEditPopover({
  stakeholder,
  anchorRef,
  leftPct,
  onSave,
  onDelete,
  onClose,
}: {
  stakeholder: Stakeholder;
  /** The stakeholder's chip on the plot: the popover opens beside it. */
  anchorRef: RefObject<HTMLButtonElement | null>;
  leftPct: number;
  /** With `grantViewerIfMissing`, a promise that settles with the save. */
  onSave: (
    patch: Partial<Stakeholder>,
    options?: WidgetChangeOptions,
  ) => void | Promise<void>;
  onDelete: () => void;
  onClose: () => void;
}): ReactElement {
  const people = usePeopleSource();
  const [draft, setDraft] = useState<StakeholderDraft>({
    name: stakeholder.name,
    userId: stakeholder.userId ?? null,
    role: stakeholder.role ?? "",
    quadrant: stakeholder.quadrant,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * PB-4923 AC5 — the three leaves this popover edits, on a stakeholder that
   * already exists. `StakeholderAddForm` below leases nothing: its row has no
   * id until it is added. `note` is a mapped field with no control on this
   * surface, and the QUADRANT DRAG on the grid is deliberately not leased —
   * same-field drag is the disputed policy (G1) and is not settled here.
   */
  const target = (field: string): string =>
    widgetTargetKey([
      { kind: "item", collection: "stakeholders", itemId: stakeholder.id },
      { kind: "prop", name: field },
    ]);
  const nameLease = useFieldEditSession(target("name"));
  const roleLease = useFieldEditSession(target("role"));
  const quadrantLease = useFieldEditSession(target("quadrant"));

  const save = (): void => {
    const name = draft.name.trim();
    if (!name || saving) return;
    const role = draft.role.trim();
    const saved = onSave(
      {
        name,
        // Without a canvas people source the name stays text alone.
        ...(people ? { userId: draft.userId } : {}),
        quadrant: draft.quadrant,
        ...(role ? { role } : { role: undefined }),
      },
      people ? grantFor(draft.userId, stakeholder.userId) : undefined,
    );
    if (!(saved instanceof Promise)) {
      onClose();
      return;
    }
    setSaving(true);
    saved.then(onClose, (reason: unknown) => {
      setSaving(false);
      setError(refusal(reason));
    });
  };

  const inputClass =
    "w-full rounded-md border border-pb-border bg-pb-page/40 px-2.5 py-1.5 text-xs text-pb-text focus-ring";

  /* The design-system Popover places the panel beside the chip, paints its
     surface and closes on Escape or an outside press (look row OK-61). */
  return (
    <Popover
      anchorRef={anchorRef}
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      contentRole="dialog"
      placement={leftPct < 55 ? "right" : "left"}
      wrapperClassName="absolute"
    >
      <PopoverContent
        aria-label={`Edit ${stakeholder.name}`}
        padding="sm"
        sideOffset={12}
        className="flex w-55 flex-col gap-2"
        onPointerDown={(event) => event.stopPropagation()}
      >
        {people ? (
          <StakeholderNameField
            source={people}
            draft={draft}
            storedUserId={stakeholder.userId}
            error={error}
            {...nameLease}
            onChange={(next) => {
              setError(null);
              setDraft((current) => ({ ...current, ...next }));
            }}
          />
        ) : (
          /* eslint-disable-next-line ds/no-hand-built -- kept: a 30px field on a translucent page fill with a hairline border; Input is a 36px card-fill field on the control border, and no approved look row lists the change. */
          <input
            aria-label="Stakeholder name"
            value={draft.name}
            {...nameLease}
            onChange={(event) =>
              setDraft((current) => ({ ...current, name: event.target.value }))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") save();
            }}
            placeholder="Name"
            className={inputClass}
          />
        )}
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a 30px field on a translucent page fill with a hairline border; Input is a 36px card-fill field on the control border, and no approved look row lists the change. */}
        <input
          aria-label="Stakeholder role"
          value={draft.role}
          {...roleLease}
          onChange={(event) =>
            setDraft((current) => ({ ...current, role: event.target.value }))
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") save();
          }}
          placeholder="Role / org (optional)"
          className={inputClass}
        />
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a native select on a translucent page fill with the OS arrow; NativeSelect is a 36px card-fill field with its own chevron, and no approved look row lists the change. */}
        <select
          aria-label="Stakeholder quadrant"
          value={draft.quadrant}
          {...quadrantLease}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              quadrant: event.target.value as StakeholderQuadrant,
            }))
          }
          className={inputClass}
        >
          {QUADRANTS.map((quadrant) => (
            <option key={quadrant.key} value={quadrant.key}>
              {quadrant.label}
            </option>
          ))}
        </select>
        <div className="flex items-center justify-between gap-2 pt-0.5">
          {/* eslint-disable-next-line ds/no-hand-built -- kept: a muted delete action with a red hover wash; Button ghost critical xs is 11px text with the 8px corner, and no approved look row lists the change. */}
          <button
            type="button"
            aria-label="Delete stakeholder"
            onClick={onDelete}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-pb-text-muted hover:bg-pb-red/10 hover:text-pb-red-text focus-ring"
          >
            <Trash2 className="size-3.5" /> Delete
          </button>
          <div className="flex items-center gap-1">
            {/* eslint-disable-next-line ds/no-hand-built -- kept: a 24px muted text action with a 4px corner; Button ghost sm is 28px in secondary ink, and no approved look row lists the change. */}
            <button
              type="button"
              onClick={onClose}
              className="rounded px-2 py-1 text-xs text-pb-text-muted hover:pb-overlay-paint hover:text-pb-text"
            >
              Cancel
            </button>
            {/* eslint-disable-next-line ds/no-hand-built -- kept: a solid red 24px commit with a 4px corner and 12px text; Button destructive xs is 11px text with the 8px corner, and no approved look row lists the change. */}
            <button
              type="button"
              onClick={save}
              disabled={!draft.name.trim() || saving}
              className="rounded bg-pb-red px-2.5 py-1 text-xs font-medium text-pb-on-accent hover:bg-pb-red/90 disabled:opacity-40"
            >
              Save
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function StakeholderAddForm({
  variant = "overlay",
  readOnly,
  onAdd,
  onCancel,
}: {
  variant?: "overlay" | "panel";
  readOnly?: boolean | undefined;
  /** A promise when the draft picks a person: the form waits on that save. */
  onAdd: (draft: StakeholderDraft) => void | Promise<void>;
  onCancel: () => void;
}): ReactElement {
  const people = usePeopleSource();
  const [draft, setDraft] = useState<StakeholderDraft>({
    name: "",
    userId: null,
    role: "",
    quadrant: "manage",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = (): void => {
    if (readOnly || saving || !draft.name.trim()) return;
    const saved = onAdd(draft);
    if (!(saved instanceof Promise)) return;
    setSaving(true);
    saved.catch((reason: unknown) => {
      setSaving(false);
      setError(refusal(reason));
    });
  };
  const inputClass =
    "w-full rounded-md border border-pb-border bg-pb-page/40 px-2.5 py-1.5 text-xs text-pb-text focus-ring disabled:opacity-50";

  return (
    <div
      className={
        variant === "overlay"
          ? "absolute end-2 top-2 z-20 flex w-60 flex-col gap-2 rounded-md border border-pb-border bg-pb-card p-2.5 shadow-xl"
          : "flex shrink-0 flex-col gap-2 rounded-md border border-pb-border bg-pb-card-elevated/60 p-3"
      }
      onPointerDown={(event) => event.stopPropagation()}
    >
      {people ? (
        <StakeholderNameField
          source={people}
          draft={draft}
          storedUserId={null}
          error={error}
          disabled={readOnly}
          onChange={(next) => {
            setError(null);
            setDraft((current) => ({ ...current, ...next }));
          }}
        />
      ) : (
        /* eslint-disable-next-line ds/no-hand-built -- kept: a 30px field on a translucent page fill with a hairline border; Input is a 36px card-fill field on the control border, and no approved look row lists the change. */
        <input
          aria-label="Stakeholder name"
          value={draft.name}
          disabled={readOnly}
          onChange={(event) =>
            setDraft((current) => ({ ...current, name: event.target.value }))
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
            if (event.key === "Escape") onCancel();
          }}
          placeholder="Name (e.g. Plant Manager)"
          className={inputClass}
        />
      )}
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a 30px field on a translucent page fill with a hairline border; Input is a 36px card-fill field on the control border, and no approved look row lists the change. */}
      <input
        aria-label="Stakeholder role"
        value={draft.role}
        disabled={readOnly}
        onChange={(event) =>
          setDraft((current) => ({ ...current, role: event.target.value }))
        }
        onKeyDown={(event) => {
          if (event.key === "Enter") submit();
        }}
        placeholder="Role / org (optional)"
        className={inputClass}
      />
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a native select on a translucent page fill with the OS arrow; NativeSelect is a 36px card-fill field with its own chevron, and no approved look row lists the change. */}
      <select
        aria-label="Stakeholder quadrant"
        value={draft.quadrant}
        disabled={readOnly}
        onChange={(event) =>
          setDraft((current) => ({
            ...current,
            quadrant: event.target.value as StakeholderQuadrant,
          }))
        }
        className={inputClass}
      >
        {QUADRANTS.map((quadrant) => (
          <option key={quadrant.key} value={quadrant.key}>
            {quadrant.label} — {quadrant.caption}
          </option>
        ))}
      </select>
      <div className="flex justify-end gap-2 pt-1">
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a 24px muted text action with a 4px corner; Button ghost sm is 28px in secondary ink, and no approved look row lists the change. */}
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-2.5 py-1 text-xs text-pb-text-muted hover:pb-overlay-paint hover:text-pb-text"
        >
          Cancel
        </button>
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a solid red 24px commit with a 4px corner and 12px text; Button destructive xs is 11px text with the 8px corner, and no approved look row lists the change. */}
        <button
          type="button"
          aria-label="Confirm add stakeholder"
          onClick={submit}
          disabled={readOnly || saving || !draft.name.trim()}
          className="rounded-md bg-pb-red px-3 py-1 text-xs font-medium text-pb-on-accent hover:bg-pb-red/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );
}

export function StakeholderMapWidget({
  data,
  onChange,
  readOnly,
  headerActionOpen,
  onHeaderActionOpenChange,
  compact = true,
}: WidgetBodyProps<StakeholderMapParams> & {
  compact?: boolean | undefined;
}): ReactElement {
  const stageRef = useRef<HTMLDivElement | null>(null);
  /** The chip whose editor is open: the edit popover's anchor. */
  const editAnchorRef = useRef<HTMLButtonElement | null>(null);
  const dataRef = useRef(data);
  const cleanupDragRef = useRef<(() => void) | null>(null);
  const [livePosition, setLivePosition] = useState<{
    id: string;
    leftPct: number;
    topPct: number;
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previousReadOnly, setPreviousReadOnly] = useState(readOnly);

  if (readOnly !== previousReadOnly) {
    setPreviousReadOnly(readOnly);
    if (readOnly) {
      setLivePosition(null);
      setEditingId(null);
    }
  }

  useEffect(() => () => cleanupDragRef.current?.(), []);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  useEffect(() => {
    if (!readOnly) return;
    cleanupDragRef.current?.();
  }, [readOnly]);
  const visibleEditingId = data.stakeholders.some(
    (stakeholder) => stakeholder.id === editingId,
  )
    ? editingId
    : null;
  const visibleLivePosition = readOnly ? null : livePosition;
  const visibleAddForm = !readOnly && headerActionOpen === true;

  const byQuad: Record<StakeholderQuadrant, Stakeholder[]> = {
    satisfy: [],
    manage: [],
    monitor: [],
    inform: [],
  };
  for (const stakeholder of data.stakeholders) {
    byQuad[stakeholder.quadrant].push(stakeholder);
  }

  const beginDrag = (
    event: ReactPointerEvent<HTMLButtonElement>,
    stakeholder: Stakeholder,
  ): void => {
    if (readOnly || event.button !== 0 || !stageRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = stageRef.current.getBoundingClientRect();
    const start = { x: event.clientX, y: event.clientY };
    let moved = false;
    const positionFor = (clientX: number, clientY: number) => ({
      x: clamp((clientX - rect.left) / rect.width, 0, 1),
      y: clamp((clientY - rect.top) / rect.height, 0, 1),
    });
    const cleanup = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      cleanupDragRef.current = null;
    };
    const move = (pointerEvent: PointerEvent): void => {
      if (
        Math.abs(pointerEvent.clientX - start.x) +
          Math.abs(pointerEvent.clientY - start.y) >
        4
      ) {
        moved = true;
      }
      if (!moved) return;
      const position = positionFor(pointerEvent.clientX, pointerEvent.clientY);
      setLivePosition({
        id: stakeholder.id,
        leftPct: position.x * 100,
        topPct: position.y * 100,
      });
    };
    const finish = (pointerEvent: PointerEvent): void => {
      cleanup();
      setLivePosition(null);
      if (!moved) {
        setEditingId(stakeholder.id);
        return;
      }
      const position = positionFor(pointerEvent.clientX, pointerEvent.clientY);
      const quadrant = quadrantFromPosition(position.x, position.y);
      if (quadrant === stakeholder.quadrant) return;
      const current = dataRef.current;
      onChange({
        ...current,
        stakeholders: current.stakeholders.map((candidate) =>
          candidate.id === stakeholder.id
            ? { ...candidate, quadrant }
            : candidate,
        ),
      });
    };
    const cancel = (): void => {
      cleanup();
      setLivePosition(null);
    };
    cleanupDragRef.current?.();
    cleanupDragRef.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", cancel);
  };

  const addStakeholder = (draft: StakeholderDraft): void | Promise<void> => {
    const name = draft.name.trim();
    if (readOnly || !name) return;
    const role = draft.role.trim();
    const options = grantFor(draft.userId, null);
    const next = {
      ...data,
      stakeholders: [
        ...data.stakeholders,
        {
          id: `stake-${nanoid(8)}`,
          name,
          ...(draft.userId ? { userId: draft.userId } : {}),
          quadrant: draft.quadrant,
          ...(role ? { role } : {}),
        },
      ],
    };
    const saved = options ? onChange(next, options) : onChange(next);
    const close = () => onHeaderActionOpenChange?.(false);
    // A pick's save is awaited: a refusal keeps the form open.
    if (options) return Promise.resolve(saved).then(close);
    close();
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 gap-1.5">
        <div className="flex shrink-0 flex-col items-center justify-between py-2">
          <span className="text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
            High
          </span>
          <span
            className="text-dense-1 font-semibold uppercase tracking-wider text-pb-text-muted"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Power
          </span>
          <span className="text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
            Low
          </span>
        </div>
        {/* Kept hand-built: the power/interest plot, the stage the stakeholder chips are placed and dragged on. */}
        <div
          ref={stageRef}
          data-testid="stakeholder-map-grid"
          className="relative min-h-0 flex-1 overflow-hidden rounded-md border border-pb-border bg-pb-card-elevated/30"
        >
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
            {QUADRANTS.map((quadrant) => (
              <div
                key={quadrant.key}
                className={`relative border border-pb-border/50 ${quadrant.cellBg}`}
              >
                {!compact ? (
                  <span className="absolute start-2 top-1.5 text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
                    {quadrant.label}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          <div className="absolute inset-x-0 top-1/2 h-px bg-pb-border" />
          <div className="absolute inset-y-0 start-1/2 w-px bg-pb-border" />
          {QUADRANTS.flatMap((quadrant) => {
            const group = byQuad[quadrant.key];
            return group.map((stakeholder, index) => {
              const placed = placeInQuadrant(quadrant.key, index, group.length);
              const live =
                visibleLivePosition?.id === stakeholder.id
                  ? visibleLivePosition
                  : null;
              return (
                // The Tooltip's wrapper carries the chip's place on the plot.
                <Tooltip
                  key={stakeholder.id}
                  content={
                    stakeholder.role
                      ? `${stakeholder.name} — ${stakeholder.role}`
                      : stakeholder.name
                  }
                  wrapperClassName="absolute -translate-x-1/2 -translate-y-1/2"
                  wrapperStyle={{
                    left: `${live?.leftPct ?? placed.leftPct}%`,
                    top: `${live?.topPct ?? placed.topPct}%`,
                  }}
                >
                  {/* eslint-disable-next-line ds/no-hand-built -- kept: a draggable stakeholder chip on the plot (a canvas drag handle). */}
                  <button
                    ref={
                      visibleEditingId === stakeholder.id
                        ? editAnchorRef
                        : undefined
                    }
                    type="button"
                    disabled={readOnly}
                    onPointerDown={(event) => beginDrag(event, stakeholder)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setEditingId(stakeholder.id);
                    }}
                    className={`max-w-35 touch-none select-none truncate whitespace-nowrap rounded-full border px-2 py-0.5 text-dense-1 font-medium shadow-sm disabled:cursor-default ${
                      readOnly
                        ? "cursor-default"
                        : "cursor-grab active:cursor-grabbing"
                    } ${CHIP_BY_QUADRANT[stakeholder.quadrant]} ${
                      visibleEditingId === stakeholder.id
                        ? "ring-2 ring-pb-red/60"
                        : ""
                    }`}
                    aria-label={`Move ${stakeholder.name}`}
                  >
                    {stakeholder.name}
                  </button>
                </Tooltip>
              );
            });
          })}
          {(() => {
            if (readOnly || !visibleEditingId) return null;
            const stakeholder = data.stakeholders.find(
              (candidate) => candidate.id === visibleEditingId,
            );
            if (!stakeholder) return null;
            const group = byQuad[stakeholder.quadrant];
            const position = placeInQuadrant(
              stakeholder.quadrant,
              group.findIndex((candidate) => candidate.id === stakeholder.id),
              group.length,
            );
            return (
              <StakeholderEditPopover
                key={stakeholder.id}
                stakeholder={stakeholder}
                anchorRef={editAnchorRef}
                leftPct={position.leftPct}
                onClose={() => setEditingId(null)}
                onSave={(patch, options) => {
                  const next = {
                    ...data,
                    stakeholders: data.stakeholders.map((candidate) =>
                      candidate.id === stakeholder.id
                        ? { ...candidate, ...patch }
                        : candidate,
                    ),
                  };
                  if (!options) {
                    onChange(next);
                    return undefined;
                  }
                  return Promise.resolve(onChange(next, options));
                }}
                onDelete={() => {
                  onChange({
                    ...data,
                    stakeholders: data.stakeholders.filter(
                      (candidate) => candidate.id !== stakeholder.id,
                    ),
                  });
                  setEditingId(null);
                }}
              />
            );
          })()}
          {data.stakeholders.length === 0 ? (
            <div className="pointer-events-none absolute inset-0 grid place-items-center px-3 text-center text-xs italic text-pb-text-muted">
              {compact
                ? "Click + Add stakeholder above to drop one onto the grid."
                : "Click Add to drop a stakeholder onto the grid."}
            </div>
          ) : null}
          {visibleAddForm ? (
            <StakeholderAddForm
              readOnly={readOnly}
              onAdd={addStakeholder}
              onCancel={() => onHeaderActionOpenChange?.(false)}
            />
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-between ps-8 pe-1 pt-1.5">
        <span className="text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
          Low
        </span>
        <span className="text-dense-1 font-semibold uppercase tracking-wider text-pb-text-muted">
          Interest
        </span>
        <span className="text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
          High
        </span>
      </div>
    </div>
  );
}
